// Marketing — atribución de punta a punta (FASE M, tarea M.1).
//
// La pantalla responde una sola pregunta y todo lo demás está al servicio de ella: **de dónde vino
// la gente que terminó viniendo**. No es un panel de "cuántos mensajes mandamos": eso ya está en el
// portal, y contarlo otra vez acá no le sirve a nadie.
//
// Dos vistas y no más, porque son los dos actos que existen hoy: crear el enlace que hace medible
// una campaña, y mirar el embudo que resulta.
//
// ⚠️ **Nada de lo que se ve acá se calcula en el navegador.** La ventana de atribución, qué toque se
// lleva el crédito y qué cuenta como "sin origen" viven en `auth.origen_*`. Repetir esas reglas acá
// sería tener dos verdades y descubrir la diferencia el día que no cuadren.
import { apiFetch } from './api.js';
import { Toast } from '../comun/toast.js';
import { iniciarReporteDeErrores } from '../comun/errores.js';
import { montarBotonTema } from '../comun/tema.js';
import { montarCabecera } from '../comun/cabecera.js';
import { plural } from '../comun/texto.js';
import './css/styles.css';
import './css/agenda.css';

iniciarReporteDeErrores();

const RAIZ = 'https://api.deviant.cl';
const API = {
  enlaces: `${RAIZ}/portal/marketing/enlaces`,
  enlace: `${RAIZ}/portal/marketing/enlace`,
  embudo: `${RAIZ}/portal/marketing/embudo`,
  costos: `${RAIZ}/portal/marketing/costos`,
  gastos: `${RAIZ}/portal/marketing/gastos`,
  gasto: `${RAIZ}/portal/marketing/gasto`,
  gastoBorrar: `${RAIZ}/portal/marketing/gasto/borrar`,
  csv: `${RAIZ}/portal/marketing/conversiones.csv`,
  paginas: `${RAIZ}/portal/agenda/paginas`,
  instancias: `${RAIZ}/portal/estado`,
  logout: `${RAIZ}/portal/logout`,
};

const $ = (id) => document.getElementById(id);
const el = (t, c, x) => {
  const n = document.createElement(t);
  if (c) n.className = c;
  if (x != null) n.textContent = x;
  return n;
};
const icono = (n) => { const i = el('i', `ti ${n}`); i.setAttribute('aria-hidden', 'true'); return i; };
const num = (v) => Number(v ?? 0);

const estado = { enlaces: [], paginas: [], instancias: [] };

// ---------------------------------------------------------------------------
// Vistas
// ---------------------------------------------------------------------------

async function irA(vista) {
  for (const b of document.querySelectorAll('.crm-menu-item[data-vista]')) {
    b.classList.toggle('activa', b.dataset.vista === vista);
  }
  for (const s of document.querySelectorAll('.crm-seccion')) {
    s.hidden = s.dataset.seccion !== vista;
  }
  if (location.hash !== `#${vista}`) history.replaceState(null, '', `#${vista}`);
  // Un `if/else` de dos ramas mandaba cualquier vista que no fuera «embudo» a cargar enlaces. Con
  // tres vistas eso deja de ser una simplificación y pasa a ser un error silencioso.
  if (vista === 'embudo') await cargarEmbudo();
  else if (vista === 'costos') await cargarCostos();
  else await cargarEnlaces();
}

// ---------------------------------------------------------------------------
// Embudo
// ---------------------------------------------------------------------------

/**
 * Una fila del embudo.
 *
 * ⚠️ Los cinco números van **en la misma línea y en orden**, no en tarjetas sueltas: el valor de
 * esto es la caída de uno al siguiente. Separados, cada cifra se lee como un logro y nadie ve dónde
 * se pierde la gente, que es lo único accionable.
 */
function filaEmbudo(f) {
  const esSinOrigen = !f.enlace_id && f.origen === 'Sin origen';
  const caja = el('div', `crm-bloque mk-fila${esSinOrigen ? ' mk-fila-sin' : ''}`);

  const cab = el('div', 'mk-fila-cab');
  const tit = el('div', 'mk-fila-tit');
  if (f.canal) tit.appendChild(icono(iconoCanal(f.canal)));
  tit.appendChild(el('span', null, f.origen));
  cab.appendChild(tit);
  caja.appendChild(cab);

  // Los pasos, con el número de arriba como denominador. `toques` es 0 en la fila sin origen —esa
  // gente llegó sin que nada dijera de dónde— así que ahí el embudo arranca en "personas".
  const pasos = [
    ['Llegadas', num(f.toques), 'ti-world'],
    ['Personas', num(f.contactos), 'ti-users'],
    ['Conversaciones', num(f.conversaciones), 'ti-message-2'],
    ['Horas', num(f.citas), 'ti-calendar'],
    ['Vinieron', num(f.atendidas), 'ti-check'],
  ];
  const base = num(f.toques) || num(f.contactos) || 1;
  const grilla = el('div', 'mk-pasos');
  for (const [nombre, valor, ico] of pasos) {
    const p = el('div', 'mk-paso');
    p.appendChild(icono(ico));
    p.appendChild(el('div', 'mk-paso-n', String(valor)));
    p.appendChild(el('div', 'mk-paso-txt', nombre));
    const barra = el('div', 'crm-barra');
    // ⚠️ `crm-barra-cero` no es cosmético: sin esa clase la regla lleva `min-width: 2px`, así que un
    // cero se dibuja como una barrita visible. Es exactamente el paso donde el negocio tiene que ver
    // que no llegó nadie, y una barra dice lo contrario del número que tiene arriba.
    const llena = el('div', `crm-barra-llena${valor ? '' : ' crm-barra-cero'}`);
    llena.style.width = `${Math.min(100, Math.round((valor / base) * 100))}%`;
    barra.appendChild(llena);
    p.appendChild(barra);
    grilla.appendChild(p);
  }
  caja.appendChild(grilla);

  // ⚠️ La línea de abajo es la que impide leer mal el embudo. `sin_marcar` no es una inasistencia:
  // es una hora pasada que nadie cerró, y sin decirlo el negocio que no marca asistencia lee cero
  // en "Vinieron" y concluye que la campaña no sirvió.
  const notas = [];
  if (num(f.sin_marcar)) {
    notas.push(`${f.sin_marcar} hora(s) pasada(s) sin marcar: no se sabe si vinieron`);
  }
  if (num(f.no_asistio)) notas.push(`${f.no_asistio} no asistió`);
  if (num(f.recompra)) notas.push(`${f.recompra} volvió una segunda vez`);
  if (notas.length) caja.appendChild(el('p', 'agente-ayuda mk-nota', notas.join(' · ')));

  if (esSinOrigen) {
    caja.appendChild(el('p', 'agente-ayuda',
      'Gente que llegó sin que nada dijera de dónde: al número directo, por un enlace sin '
      + 'código, o antes de que esto existiera. No es una campaña que funcionó mal: es lo que '
      + 'todavía no se puede medir.'));
  }
  return caja;
}

function iconoCanal(c) {
  return {
    pagina: 'ti-world', whatsapp: 'ti-brand-whatsapp', telegram: 'ti-brand-telegram',
    web_chat: 'ti-message-2', email: 'ti-mail', instagram: 'ti-brand-instagram',
    messenger: 'ti-brand-messenger',
  }[c] ?? 'ti-point';
}

async function cargarEmbudo() {
  const caja = $('mk-embudo');
  caja.textContent = 'Calculando…';
  let r;
  try {
    r = await apiFetch(`${API.embudo}?dias=${$('mk-dias').value}`,
      { credentials: 'include', hideErrorToast: true });
  } catch { caja.textContent = 'No se pudo calcular el embudo.'; return; }
  caja.innerHTML = '';

  // Las filas con algo primero: una campaña sin una sola llegada no tiene por qué empujar hacia
  // abajo a la que sí trajo citas.
  const filas = [...(r.embudo ?? [])].sort((a, b) => {
    const sinA = a.origen === 'Sin origen' && !a.enlace_id;
    const sinB = b.origen === 'Sin origen' && !b.enlace_id;
    if (sinA !== sinB) return sinA ? 1 : -1;   // "Sin origen" siempre al final.
    return num(b.citas) - num(a.citas) || num(b.toques) - num(a.toques);
  });

  const conOrigen = filas.filter((f) => !(f.origen === 'Sin origen' && !f.enlace_id));
  if (!conOrigen.length) {
    const v = el('div', 'crm-bloque');
    v.appendChild(el('h2', null, 'Todavía no hay ningún origen'));
    v.appendChild(el('p', 'agente-ayuda',
      'Crea un enlace en «Orígenes» y publícalo en tu anuncio o tu bio. Desde ahí, todo lo que '
      + 'entre por ese enlace queda contado hasta la hora atendida.'));
    caja.appendChild(v);
  }
  for (const f of filas) caja.appendChild(filaEmbudo(f));
}

// ---------------------------------------------------------------------------
// Costos (M.7)
// ---------------------------------------------------------------------------
//
// ⚠️ **La unidad es la cita y nunca la persona.** Quien agenda tres veces son tres citas; dividir el
// gasto por personas da un número más bonito y equivocado. Y no se dice «paciente» en ninguna
// cadena: esto no es un producto para clínicas — un taller, una peluquería y un estudio de tatuajes
// agendan citas igual.
//
// Tres reglas de presentación, las tres de la disciplina de C.5 —decir qué NO se está midiendo—
// aplicadas a una pantalla con la que alguien decide en qué gasta su plata:
//
//   1. Sin gasto declarado se escribe «sin gasto declarado», no un cero. Un cero se lee como gratis.
//   2. Con citas pasadas sin cerrar, el costo por cita atendida **es un rango** y se dibuja como
//      rango. Un solo número ahí sería el peor caso disfrazado de dato.
//   3. El gasto que no cuelga de una campaña se muestra aparte y se dice que no se puede repartir.

const plata = (n, m) => (n == null ? null : `${Number(n).toLocaleString('es-CL')} ${m ?? 'CLP'}`);

async function cargarCostos() {
  const caja = $('mk-costos');
  caja.textContent = 'Calculando…';
  // ⚠️ El desplegable de campañas se llena desde `estado.enlaces`, que hasta acá sólo cargaba
  // `cargarEnlaces()`. Quien entra directo a Costos —o con `#costos` en la URL— se encontraba la
  // lista vacía y ninguna forma de asociar el gasto a su campaña, sin un solo error a la vista.
  if (!(estado.enlaces ?? []).length) {
    try {
      const e = await apiFetch(API.enlaces, { credentials: 'include', hideErrorToast: true });
      estado.enlaces = e.enlaces ?? [];
    } catch { estado.enlaces = []; }
  }
  let r;
  try {
    r = await apiFetch(`${API.costos}?dias=${$('mk-costos-dias').value}`,
      { credentials: 'include', hideErrorToast: true });
  } catch { caja.textContent = 'No se pudo calcular los costos.'; return; }
  caja.innerHTML = '';

  const filas = r.costos ?? [];
  const conGasto = filas.filter((f) => f.gasto != null);
  if (!conGasto.length) {
    const v = el('div', 'crm-bloque');
    v.appendChild(el('h2', null, 'Todavía no has declarado ningún gasto'));
    v.appendChild(el('p', 'agente-ayuda',
      'Carga lo que gastaste con «+ Gasto» y acá aparece cuánto te costó cada cita '
      + 'que trajo. Si además le pusiste un enlace propio en «Orígenes», el costo sale por campaña '
      + 'en vez de junto.'));
    caja.appendChild(v);
  }
  for (const f of filas) caja.appendChild(filaCosto(f));
  await cargarGastos();
}

function filaCosto(f) {
  const item = el('div', 'cfg-item');
  const izq = el('div', 'mk-enlace-izq');

  const l1 = el('div', 'cfg-item-linea');
  l1.appendChild(el('span', 'cfg-item-titulo', f.origen));
  if (f.gasto != null) l1.appendChild(el('span', 'cfg-chip', plata(f.gasto, f.moneda)));
  izq.appendChild(l1);

  // El gasto que no se puede atribuir. Se dice con palabras en vez de repartirlo entre las campañas:
  // repartir lo que no sabemos de dónde salió cambia el costo de todas por una suposición.
  if (f.atribuible === false) {
    const l = el('div', 'cfg-item-linea');
    l.appendChild(el('span', 'cfg-item-sub',
      'Sin campaña asociada, así que no se reparte entre citas. Para que cuente, '
      + 'crea un enlace en «Orígenes» y vuelve a cargar el gasto eligiéndolo.'));
    izq.appendChild(l);
    item.appendChild(izq);
    return item;
  }

  const l2 = el('div', 'cfg-item-linea');
  l2.appendChild(el('span', 'cfg-item-sub',
    `${plural(f.citas, 'cita', 'citas')} · ${plural(f.atendidas, 'atendida', 'atendidas')}`
    + (Number(f.sin_marcar) ? ` · ${f.sin_marcar} sin cerrar` : '')));
  izq.appendChild(l2);

  const l3 = el('div', 'cfg-item-linea');
  if (f.gasto == null) {
    l3.appendChild(el('span', 'cfg-item-sub', 'Sin gasto declarado para este período.'));
  } else {
    l3.appendChild(el('strong', 'mk-costo',
      f.costo_cita != null ? `${plata(f.costo_cita, f.moneda)} por cita agendada` : 'Sin citas en el período'));
  }
  izq.appendChild(l3);

  if (f.gasto != null && (f.costo_atendida != null || f.costo_atendida_min != null)) {
    const l4 = el('div', 'cfg-item-linea');
    // ⚠️ El rango. Con citas pasadas sin cerrar, las atendidas de verdad están entre `atendidas` y
    // `atendidas + sin_marcar`, así que el costo está entre dos valores. Mostrar sólo el alto
    // castigaría a la campaña por algo que hizo el negocio —no marcar—, y mostrar sólo el bajo
    // inventaría asistencias.
    const rango = Number(f.sin_marcar) > 0 && f.costo_atendida_min != null && f.costo_atendida != null;
    l4.appendChild(el('strong', 'mk-costo',
      rango
        ? `entre ${plata(f.costo_atendida_min, f.moneda)} y ${plata(f.costo_atendida, f.moneda)} por cita atendida`
        : `${plata(f.costo_atendida ?? f.costo_atendida_min, f.moneda)} por cita atendida`));
    izq.appendChild(l4);
    if (rango) {
      const l5 = el('div', 'cfg-item-linea');
      l5.appendChild(el('span', 'cfg-item-sub',
        `Es un rango porque hay ${f.sin_marcar} cita(s) pasadas que nadie cerró. Márcalas en el `
        + 'calendario y el número deja de ser un rango.'));
      izq.appendChild(l5);
    }
  }

  item.appendChild(izq);
  return item;
}

async function cargarGastos() {
  const caja = $('mk-gastos');
  caja.innerHTML = '';
  let r;
  try {
    r = await apiFetch(API.gastos, { credentials: 'include', hideErrorToast: true });
  } catch { caja.textContent = 'No se pudo cargar la lista de gastos.'; return; }
  const gastos = r.gastos ?? [];
  if (!gastos.length) {
    caja.appendChild(el('p', 'agente-ayuda', 'Todavía no hay gastos cargados.'));
    return;
  }
  for (const g of gastos) {
    const item = el('div', 'cfg-item');
    const izq = el('div', 'mk-enlace-izq');
    const l1 = el('div', 'cfg-item-linea');
    l1.appendChild(el('span', 'cfg-item-titulo', plata(g.monto, g.moneda)));
    l1.appendChild(el('span', 'cfg-chip', g.plataforma));
    izq.appendChild(l1);
    const l2 = el('div', 'cfg-item-linea');
    l2.appendChild(el('span', 'cfg-item-sub',
      `${g.desde} a ${g.hasta} · ${g.campana || 'sin campaña asociada'}${g.nota ? ` · ${g.nota}` : ''}`));
    izq.appendChild(l2);
    item.appendChild(izq);

    const acc = el('div', 'cfg-item-acciones');
    const editar = el('button', 'btn-sec btn-sm', 'Editar');
    editar.type = 'button';
    editar.addEventListener('click', () => abrirEditorGasto(g));
    acc.appendChild(editar);
    const borrar = el('button', 'btn-sec btn-sm', 'Borrar');
    borrar.type = 'button';
    borrar.addEventListener('click', async () => {
      if (!window.confirm('¿Borrar este gasto? Los costos se recalculan sin él.')) return;
      try {
        await apiFetch(API.gastoBorrar, {
          method: 'POST', credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: g.id }), hideErrorToast: true,
        });
        Toast.success('Gasto borrado.');
        await cargarCostos();
      } catch (e) { Toast.error(e?.mensaje || 'No se pudo borrar.'); }
    });
    acc.appendChild(borrar);
    item.appendChild(acc);
    caja.appendChild(item);
  }
}

function abrirEditorGasto(g) {
  const caja = $('mk-gasto-editor');
  caja.innerHTML = '';
  caja.hidden = false;

  const campo = (etiqueta, nodo, ayuda) => {
    const l = document.createElement('label');
    l.className = 'ag-campo';
    l.appendChild(el('span', null, etiqueta));
    l.appendChild(nodo);
    if (ayuda) l.appendChild(el('span', 'agente-ayuda', ayuda));
    return l;
  };
  const inp = (tipo, valor, extra = {}) =>
    Object.assign(document.createElement('input'), { type: tipo, value: valor ?? '', ...extra });

  const monto = inp('number', g?.monto ?? '', { min: '1', step: '1', placeholder: '300000' });
  const desde = inp('date', g?.desde ?? '');
  const hasta = inp('date', g?.hasta ?? '');
  const nota = inp('text', g?.nota ?? '', { maxLength: 120, placeholder: 'Campaña de agosto' });

  const plataforma = document.createElement('select');
  for (const [id, nombre] of [['meta', 'Meta (Facebook e Instagram)'], ['google', 'Google'], ['otro', 'Otro']]) {
    const o = document.createElement('option');
    o.value = id; o.textContent = nombre;
    if (id === (g?.plataforma ?? 'meta')) o.selected = true;
    plataforma.appendChild(o);
  }

  const enlace = document.createElement('select');
  const vacia = document.createElement('option');
  vacia.value = ''; vacia.textContent = 'Sin campaña — no se podrá dividir por cita';
  enlace.appendChild(vacia);
  for (const e of estado.enlaces ?? []) {
    const o = document.createElement('option');
    o.value = e.id; o.textContent = e.nombre;
    if (e.id === g?.enlace_id) o.selected = true;
    enlace.appendChild(o);
  }

  const form = el('div', 'crm-bloque');
  form.appendChild(el('h2', null, g ? 'Editar gasto' : 'Nuevo gasto'));
  form.appendChild(campo('Cuánto gastaste', monto, 'Sólo el número, sin puntos. En pesos.'));
  form.appendChild(campo('Dónde', plataforma));
  form.appendChild(campo('En qué campaña', enlace,
    'Elige el enlace que publicaste en ese anuncio. Sin campaña el gasto se suma igual, pero '
    + 'no se reparte entre citas.'));
  form.appendChild(campo('Desde', desde));
  form.appendChild(campo('Hasta', hasta, 'Si el período se pasa de la ventana que estás mirando, se '
    + 'prorratea por días.'));
  form.appendChild(campo('Nota', nota));

  const acc = el('div', 'cfg-item-acciones');
  const guardar = el('button', 'ag-accion ag-accion-primaria', 'Guardar');
  guardar.type = 'button';
  guardar.addEventListener('click', async () => {
    guardar.disabled = true;
    try {
      await apiFetch(API.gasto, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: g?.id ?? null,
          enlace_id: enlace.value || null,
          plataforma: plataforma.value,
          desde: desde.value || null,
          hasta: hasta.value || null,
          monto: Number(monto.value) || null,
          nota: nota.value.trim() || null,
        }),
        hideErrorToast: true,
      });
      caja.hidden = true;
      Toast.success('Guardado.');
      await cargarCostos();
    } catch (e) {
      // El mensaje del servidor es el bueno: sabe si falló el monto, las fechas o la campaña.
      Toast.error(e?.mensaje || 'No se pudo guardar.');
      guardar.disabled = false;
    }
  });
  acc.appendChild(guardar);
  const cancelar = el('button', 'btn-sec btn-sm', 'Cancelar');
  cancelar.type = 'button';
  cancelar.addEventListener('click', () => { caja.hidden = true; });
  acc.appendChild(cancelar);
  form.appendChild(acc);
  caja.appendChild(form);
}

// ---------------------------------------------------------------------------
// Orígenes
// ---------------------------------------------------------------------------

function tarjetaEnlace(e) {
  // ⚠️ `.cfg-item` es un **flex en fila**: los hijos directos se ponen uno al lado del otro. Va el
  // bloque izquierdo con todo el texto y, aparte, `.cfg-item-acciones` con su `margin-left:auto`.
  // Colgando cada línea del item, el enlace terminaba a la derecha del nombre en vez de debajo.
  const item = el('div', `cfg-item${e.activo ? '' : ' inactivo'}`);
  const izq = el('div', 'mk-enlace-izq');

  const l1 = el('div', 'cfg-item-linea');
  l1.appendChild(el('span', 'cfg-item-titulo', e.nombre));
  if (!e.activo) l1.appendChild(el('span', 'cfg-chip', 'dado de baja'));
  izq.appendChild(l1);
  const acc = el('div', 'cfg-item-acciones');

  if (e.enlace) {
    const copiar = el('button', 'btn-sec btn-sm', 'Copiar enlace');
    copiar.type = 'button';
    copiar.addEventListener('click', async () => {
      try {
        await navigator.clipboard.writeText(e.enlace);
        Toast.success('Enlace copiado. Pégalo en tu anuncio.');
      } catch {
        // Sin permiso de portapapeles no hay nada que hacer desde acá, pero el enlace está a la
        // vista debajo: se puede seleccionar a mano. Callarlo dejaría a la persona apretando.
        Toast.error('No pude copiar. Selecciona el enlace de abajo.');
      }
    });
    acc.appendChild(copiar);
  }
  const editar = el('button', 'btn-sec btn-sm', 'Editar');
  editar.type = 'button';
  editar.addEventListener('click', () => abrirEditor(e));
  acc.appendChild(editar);

  const l2 = el('div', 'cfg-item-linea');
  l2.appendChild(icono(e.destino === 'whatsapp' ? 'ti-brand-whatsapp' : 'ti-world'));
  l2.appendChild(el('span', 'cfg-item-sub',
    `${plural(e.toques, 'llegada', 'llegadas')} · ${plural(e.contactos, 'persona', 'personas')} · ${plural(e.citas, 'hora', 'horas')}`));
  izq.appendChild(l2);

  if (e.enlace) {
    // ⚠️ El enlace se muestra entero y no acortado. Es lo que la persona va a pegar en Meta o en su
    // bio, y un enlace recortado con "…" se copia mal justo en la parte que lleva el código.
    izq.appendChild(el('code', 'mk-enlace', e.enlace));
  } else {
    izq.appendChild(el('p', 'agente-ayuda',
      e.destino === 'whatsapp'
        ? 'Este canal todavía no tiene número cargado, así que no se puede armar el enlace.'
        : 'La página de reserva que tenía este enlace ya no existe. Edítalo y elige otra.'));
  }

  item.appendChild(izq);
  item.appendChild(acc);
  return item;
}

function abrirEditor(e) {
  const caja = $('mk-editor');
  caja.hidden = false;
  caja.innerHTML = '';
  const b = el('div', 'crm-bloque');
  b.appendChild(el('h2', null, e ? 'Editar enlace' : 'Nuevo enlace'));

  const campo = (etiqueta, control, ayuda) => {
    const l = el('label', 'ag-campo');
    l.appendChild(el('span', null, etiqueta));
    l.appendChild(control);
    if (ayuda) l.appendChild(el('small', 'agente-ayuda', ayuda));
    b.appendChild(l);
    return control;
  };

  const nombre = el('input');
  nombre.type = 'text';
  nombre.maxLength = 80;
  nombre.value = e?.nombre ?? '';
  nombre.placeholder = 'Instagram agosto';
  campo('Nombre', nombre, 'El que vas a ver en el embudo.');

  const destino = el('select');
  for (const [v, t] of [['whatsapp', 'Que escriban por WhatsApp'], ['pagina', 'Que reserven en línea']]) {
    const o = el('option', null, t); o.value = v; destino.appendChild(o);
  }
  destino.value = e?.destino ?? 'whatsapp';
  campo('A dónde lleva', destino);

  const inst = el('select');
  for (const i of estado.instancias) {
    const o = el('option', null, i.etiqueta); o.value = i.id; inst.appendChild(o);
  }
  inst.value = e?.instancia_id ?? (estado.instancias[0]?.id ?? '');
  const campoInst = campo('Por qué canal entra', inst,
    'El mensaje llega con un código adentro: es lo que dice que esa persona vino de acá.');

  const pag = el('select');
  for (const p of estado.paginas) {
    const o = el('option', null, `${p.nombre} (/${p.slug})`); o.value = p.id; pag.appendChild(o);
  }
  pag.value = e?.pagina_id ?? (estado.paginas[0]?.id ?? '');
  const campoPag = campo('Qué página de reserva', pag);

  const fuente = el('input');
  fuente.type = 'text'; fuente.maxLength = 60; fuente.value = e?.fuente ?? '';
  fuente.placeholder = 'instagram';
  campo('Fuente (opcional)', fuente, 'Dónde lo publicas: instagram, google, volante, correo.');

  const campana = el('input');
  campana.type = 'text'; campana.maxLength = 80; campana.value = e?.campana ?? '';
  campo('Campaña (opcional)', campana, 'Para agrupar varios enlaces de la misma promoción.');

  // Sólo el campo del destino elegido: mostrar los dos deja elegir una combinación que la base
  // rechaza, y el error llegaría después de apretar guardar.
  const sincronizar = () => {
    campoInst.closest('.ag-campo').hidden = destino.value !== 'whatsapp';
    campoPag.closest('.ag-campo').hidden = destino.value !== 'pagina';
  };
  destino.addEventListener('change', sincronizar);
  sincronizar();

  if (e) {
    const l = el('label', 'ag-campo');
    const chk = el('input'); chk.type = 'checkbox'; chk.checked = e.activo;
    l.appendChild(chk);
    l.appendChild(el('span', null, 'Activo'));
    // ⚠️ Dar de baja **no borra**: los toques que ya trajo siguen contando en el embudo. Un enlace
    // dado de baja deja de aceptar llegadas nuevas, que es otra cosa que borrar la historia.
    l.appendChild(el('small', 'agente-ayuda',
      'De baja deja de contar llegadas nuevas. Lo que ya trajo sigue en el embudo.'));
    b.appendChild(l);
    e._chk = chk;
  }

  const acciones = el('div', 'ag-acciones');
  const guardar = el('button', 'ag-accion ag-accion-primaria', 'Guardar');
  guardar.type = 'button';
  guardar.addEventListener('click', async () => {
    guardar.disabled = true;
    try {
      const cuerpo = {
        id: e?.id ?? null,
        nombre: nombre.value.trim(),
        destino: destino.value,
        instancia_id: destino.value === 'whatsapp' ? inst.value : null,
        pagina_id: destino.value === 'pagina' ? pag.value : null,
        fuente: fuente.value.trim(),
        campana: campana.value.trim(),
      };
      if (e) cuerpo.activo = e._chk.checked;
      await apiFetch(API.enlace, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(cuerpo),
      });
      Toast.success(e ? 'Enlace actualizado.' : 'Enlace creado. Cópialo y publícalo.');
      caja.hidden = true;
      await cargarEnlaces();
    } catch (err) {
      Toast.error(err?.mensaje || 'No se pudo guardar.');
    } finally {
      guardar.disabled = false;
    }
  });
  const cancelar = el('button', 'ag-accion', 'Cancelar');
  cancelar.type = 'button';
  cancelar.addEventListener('click', () => { caja.hidden = true; });
  acciones.appendChild(guardar);
  acciones.appendChild(cancelar);
  b.appendChild(acciones);

  caja.appendChild(b);
  nombre.focus();
}

async function cargarEnlaces() {
  const caja = $('mk-lista');
  caja.textContent = 'Cargando…';
  let r;
  try {
    r = await apiFetch(API.enlaces, { credentials: 'include', hideErrorToast: true });
  } catch { caja.textContent = 'No se pudieron cargar los enlaces.'; return; }
  estado.enlaces = r.enlaces ?? [];
  caja.innerHTML = '';
  $('badge-origenes').textContent = estado.enlaces.length || '';
  if (!estado.enlaces.length) {
    const v = el('div', 'crm-bloque');
    v.appendChild(el('p', 'agente-ayuda',
      'Todavía no tienes ningún enlace. Crea uno y publícalo donde estés pautando: desde ahí '
      + 'vas a ver cuántas citas trajo y cuántas terminaron atendidas.'));
    caja.appendChild(v);
    return;
  }
  for (const e of estado.enlaces) caja.appendChild(tarjetaEnlace(e));
}

// ---------------------------------------------------------------------------

/**
 * Baja el CSV de conversiones para Google Ads.
 *
 * ⚠️ **No se puede resolver con un `<a href>` a secas.** El token de acceso dura 15 minutos y una
 * navegación no tiene forma de renovarlo: quien deje la pestaña abierta un rato se encontraría con
 * una página de error JSON en vez de su archivo. Tampoco sirve `apiFetch`, que siempre parsea la
 * respuesta como JSON. Así que va a mano, con el mismo reintento único por `/portal/refresh` que hace
 * `apiFetch`, y el archivo se entrega como blob.
 */
async function descargarCsv(e) {
  e.preventDefault();
  const pedir = () => fetch(API.csv, { credentials: 'include' });
  try {
    let r = await pedir();
    if (r.status === 401) {
      await fetch(`${RAIZ}/portal/refresh`, { method: 'POST', credentials: 'include' });
      r = await pedir();
    }
    if (!r.ok) throw new Error(String(r.status));
    const texto = await r.text();
    // Sólo la cabecera y la línea de parámetros: no hay ninguna conversión que subir, y un archivo
    // de dos líneas que Google rechaza es peor que decirlo acá.
    if (texto.trim().split('\n').length <= 2) {
      Toast.error('Todavía no hay conversiones que subir. Aparecen cuando alguien llega por un anuncio de Google, toma hora y se le marca la asistencia.');
      return;
    }
    const url = URL.createObjectURL(new Blob([texto], { type: 'text/csv' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = 'conversiones-google-ads.csv';
    a.click();
    URL.revokeObjectURL(url);
  } catch {
    Toast.error('No se pudo generar el archivo.');
  }
}

async function iniciar() {
  try {
    await apiFetch(`${API.embudo}?dias=30`, { credentials: 'include', hideErrorToast: true });
  } catch (e) {
    if (e?.status === 401) { location.replace('https://app.deviant.cl/'); return; }
    $('mk-cargando').hidden = true;
    $('mk-aviso').textContent = 'No se pudo cargar Marketing. Reintenta en un momento.';
    $('mk-aviso').hidden = false;
    return;
  }
  $('mk-cargando').hidden = true;
  $('mk-app').hidden = false;

  // Las páginas y los canales son lo que el editor ofrece elegir. Se piden una vez: cambian cuando
  // alguien los configura en otra pantalla, no mientras se arma un enlace.
  try {
    const p = await apiFetch(API.paginas, { credentials: 'include', hideErrorToast: true });
    estado.paginas = p.paginas ?? [];
  } catch { estado.paginas = []; }
  try {
    const i = await apiFetch(API.instancias, { credentials: 'include', hideErrorToast: true });
    estado.instancias = (i.instancias ?? []).map((x) => ({
      id: x.id,
      etiqueta: x.numero_telefono || x.identificador_externo || x.tipo_canal,
    }));
  } catch { estado.instancias = []; }

  for (const b of document.querySelectorAll('.crm-menu-item[data-vista]')) {
    b.addEventListener('click', () => irA(b.dataset.vista));
  }
  $('mk-csv').addEventListener('click', descargarCsv);
  $('mk-costos-dias').addEventListener('change', cargarCostos);
  $('mk-gasto-nuevo').addEventListener('click', () => abrirEditorGasto(null));
  $('mk-dias').addEventListener('change', cargarEmbudo);
  $('mk-nuevo').addEventListener('click', () => abrirEditor(null));
  // La cabecera comun de escritorio (logo, las cinco apps, tema y cuenta). En un telefono no se
  // dibuja: lo decide `cabecera.css`, no un condicional aca.
  montarCabecera({ app: 'marketing' });
  montarBotonTema($('tema-boton'));
  $('mk-salir').addEventListener('click', async () => {
    try { await apiFetch(API.logout, { method: 'POST', credentials: 'include', hideErrorToast: true }); }
    catch { /* igual se sale */ }
    location.replace('https://app.deviant.cl/');
  });

  const inicial = location.hash.replace('#', '');
  await irA(['embudo', 'origenes'].includes(inicial) ? inicial : 'embudo');
}

iniciar();
